import { Span, startInactiveSpan } from "@sentry/react";

declare global {
  var webkitAudioContext: (new () => AudioContext) | undefined;
}

export class AudioPlayer {
  private static audioContext: AudioContext | null = null;
  private static audioUnlocked: boolean = false;
  private static autoUnlock: boolean = true;
  private static audioSourceNodePool: {
    element: HTMLAudioElement;
    node: MediaElementAudioSourceNode;
  }[] = [];
  private static audioSourceNodePoolSize = 8;
  private static audioElementPool: HTMLAudioElement[] = [];
  private static audioElementPoolSize = 8;
  private static scratchBuffer: AudioBuffer | null = null;

  private static players: AudioPlayer[] = [];

  private gainNode: GainNode | null;
  private audioElement: HTMLAudioElement;
  private sourceNode: MediaElementAudioSourceNode | null;
  private unlocked: boolean;

  private stateVolume: number;
  private stateMuted: boolean;
  private fadeTimerId: number | null = null;

  constructor(options: AudioPlayerOptions) {
    if (AudioPlayer.audioContext == null) {
      AudioPlayer.audioContext = createAudioContext();
    }

    if (AudioPlayer.autoUnlock && !AudioPlayer.audioUnlocked) {
      AudioPlayer.unlockAudio();
    }

    // load
    if (options.enableWebAudioGain) {
      const { audioElement, sourceNode, unlocked } =
        AudioPlayer.obtainAudioSourceNode();
      this.audioElement = audioElement;
      this.sourceNode = sourceNode;
      this.unlocked = unlocked;
    } else {
      const { audioElement, unlocked } = AudioPlayer.obtainAudioElement();
      this.audioElement = audioElement;
      this.sourceNode = null;
      this.unlocked = unlocked;
    }

    // 自動再生された後に再度 `.load()` が呼び出されるのを防止する
    if (!this.unlocked) {
      const markUnlocked = () => {
        this.unlocked = true;
        this.audioElement.removeEventListener("play", markUnlocked);
      };
      this.audioElement.addEventListener("play", markUnlocked);
    }

    this.stateVolume = sanitizeFiniteVolume(options?.volume);
    this.stateMuted = options.muted ?? false;
    const volume = this.stateMuted ? 0 : this.stateVolume;

    this.audioElement.crossOrigin = options.enableWebAudioGain
      ? "anonymous"
      : null;
    this.audioElement.autoplay = options.autoplay ?? false;
    this.audioElement.loop = options.loop ?? false;
    this.audioElement.muted = this.stateMuted;

    if (options.onEnded) {
      this.audioElement.onended = options.onEnded;
    }

    if (this.sourceNode) {
      this.gainNode = createGain(AudioPlayer.audioContext, {
        defaultVolume: volume,
      });
      this.gainNode.gain.value = volume;
      this.audioElement.volume = 1;
      this.sourceNode
        .connect(this.gainNode)
        .connect(AudioPlayer.audioContext.destination);
    } else {
      this.gainNode = null;
      this.audioElement.volume = volume;
    }

    if (!options.disableMesureMetric) {
      injectMetric(this.audioElement);
    }

    this.audioElement.src = options.src;

    this.audioElement.load();
    AudioPlayer.players.push(this);
  }

  play() {
    AudioPlayer.audioContext?.resume();
    return this.audioElement.play();
  }

  pause() {
    this.audioElement.pause();
  }

  stop() {
    this.audioElement.pause();
    this.audioElement.currentTime = 0;
  }

  volume(volume: number) {
    this.stateVolume = sanitizeFiniteVolume(volume);

    const actualVolume = this.stateMuted ? 0 : this.stateVolume;
    if (this.gainNode) {
      this.gainNode.gain.value = actualVolume;
    } else {
      this.audioElement.volume = actualVolume;
    }
  }

  loop(loop: boolean) {
    this.audioElement.loop = loop;
  }

  mute(muted: boolean) {
    this.stateMuted = muted;
    this.audioElement.muted = muted;

    const actualVolume = this.stateMuted ? 0 : this.stateVolume;
    if (this.gainNode) {
      this.gainNode.gain.value = actualVolume;
    } else {
      this.audioElement.volume = actualVolume;
    }
  }

  fadeIn(duration: number = 1.0): Promise<void> {
    return new Promise((resolve) => {
      if (this.gainNode && AudioPlayer.audioContext) {
        // Web Audio API を使用したフェードイン
        const targetVolume = this.stateMuted ? 0 : this.stateVolume;
        const currentTime = AudioPlayer.audioContext.currentTime;

        this.gainNode.gain.cancelScheduledValues(currentTime);
        this.gainNode.gain.setValueAtTime(0, currentTime);
        this.gainNode.gain.linearRampToValueAtTime(
          targetVolume,
          currentTime + duration
        );

        if (this.fadeTimerId !== null) {
          window.clearTimeout(this.fadeTimerId);
        }

        this.fadeTimerId = window.setTimeout(() => {
          this.fadeTimerId = null;
          resolve();
        }, duration * 1000);
      } else {
        // HTMLAudioElement の volume を使用したフェードイン（フォールバック）
        const targetVolume = this.stateMuted ? 0 : this.stateVolume;
        const startTime = Date.now();
        const step = 16; // 約60fps

        this.audioElement.volume = 0;

        const fade = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / (duration * 1000), 1);
          this.audioElement.volume = targetVolume * progress;

          if (progress < 1) {
            window.setTimeout(fade, step);
          } else {
            resolve();
          }
        };

        fade();
      }
    });
  }

  fadeOut(duration: number = 1.0): Promise<void> {
    return new Promise((resolve) => {
      if (this.gainNode && AudioPlayer.audioContext) {
        // Web Audio API を使用したフェードアウト
        const currentTime = AudioPlayer.audioContext.currentTime;
        this.gainNode.gain.cancelScheduledValues(currentTime);
        const startVolume = this.gainNode.gain.value;
        this.gainNode.gain.setValueAtTime(startVolume, currentTime);
        this.gainNode.gain.linearRampToValueAtTime(0, currentTime + duration);

        if (this.fadeTimerId !== null) {
          window.clearTimeout(this.fadeTimerId);
        }

        this.fadeTimerId = window.setTimeout(() => {
          this.pause();
          this.fadeTimerId = null;
          resolve();
        }, duration * 1000);
      } else {
        // HTMLAudioElement の volume を使用したフェードアウト（フォールバック）
        const startVolume = this.audioElement.volume;
        const startTime = Date.now();
        const step = 16; // 約60fps

        const fade = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / (duration * 1000), 1);
          this.audioElement.volume = startVolume * (1 - progress);

          if (progress < 1) {
            window.setTimeout(fade, step);
          } else {
            this.pause();
            resolve();
          }
        };

        fade();
      }
    });
  }

  cancelFade() {
    if (this.fadeTimerId !== null) {
      window.clearTimeout(this.fadeTimerId);
      this.fadeTimerId = null;
    }

    if (this.gainNode && AudioPlayer.audioContext) {
      const currentTime = AudioPlayer.audioContext.currentTime;
      this.gainNode.gain.cancelScheduledValues(currentTime);
      const targetVolume = this.stateMuted ? 0 : this.stateVolume;
      this.gainNode.gain.setValueAtTime(targetVolume, currentTime);
    }
  }

  unload() {
    this.cancelFade();
    this.stop();
    this.gainNode?.disconnect();

    // clear AudioElement
    // a 0-second silence to stop any downloading
    this.audioElement.src =
      "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";
    this.audioElement.autoplay = false;
    this.audioElement.loop = false;
    this.audioElement.volume = 0;
    this.audioElement.muted = true;
    if (this.unlocked) {
      if (this.sourceNode) {
        AudioPlayer.audioSourceNodePool.push({
          element: this.audioElement,
          node: this.sourceNode,
        });
      } else {
        AudioPlayer.audioElementPool.push(this.audioElement);
      }
    }

    const index = AudioPlayer.players.indexOf(this);
    if (index >= 0) {
      AudioPlayer.players.splice(index, 1);
    }
  }

  private static unlockAudio() {
    if (this.audioUnlocked || !this.audioContext) {
      return;
    }

    this.audioUnlocked = false;
    this.autoUnlock = false;

    if (window.navigator.userAgent.match(/(iPhone|iPod|iPad)/i)) {
      this.initializeIosSampleRate();
    }

    this.scratchBuffer = this.audioContext.createBuffer(1, 1, 22050);

    // Call this method on touch start to create and play a buffer,
    // then check if the audio actually played to determine if
    // audio has now been unlocked on iOS, Android, etc.
    var unlock = () => {
      // Create a pool of unlocked HTML5 Audio objects that can
      // be used for playing sounds without user interaction. HTML5
      // Audio objects must be individually unlocked, as opposed
      // to the WebAudio API which only needs a single activation.
      // This must occur before WebAudio setup or the source.onended
      // event will not fire.
      if (!this.audioContext) {
        return;
      }

      while (this.audioSourceNodePool.length < this.audioSourceNodePoolSize) {
        const audioElement = new Audio();
        const node = this.audioContext.createMediaElementSource(audioElement);

        // Add the audio node to the pool.
        this.audioSourceNodePool.push({ element: audioElement, node });
      }

      while (this.audioElementPool.length < this.audioElementPoolSize) {
        const audioElement = new Audio();
        this.audioElementPool.push(audioElement);
      }

      // Loop through any assigned audio nodes and unlock them.
      for (const player of this.players) {
        if (!player.unlocked) {
          player.unlocked = true;
          player.audioElement.load();
        }
      }

      if (this.audioContext == null) {
        return;
      }

      // Create an empty buffer.
      var source = this.audioContext.createBufferSource();
      source.buffer = this.scratchBuffer;
      source.connect(this.audioContext.destination);

      source.start(0);

      // Calling resume() on a stack initiated by user gesture is what actually unlocks the audio on Android Chrome >= 55.
      this.audioContext.resume();

      // Setup a timeout to check that we are unlocked on the next event loop.
      source.onended = () => {
        source.disconnect(0);

        // Update the unlocked state and prevent this check from happening again.
        this.audioUnlocked = true;

        // Remove the touch start listener.
        document.removeEventListener("touchstart", unlock, true);
        document.removeEventListener("touchend", unlock, true);
        document.removeEventListener("click", unlock, true);
        document.removeEventListener("keydown", unlock, true);
      };
    };

    // Setup a touch start listener to attempt an unlock in.
    document.addEventListener("touchstart", unlock, true);
    document.addEventListener("touchend", unlock, true);
    document.addEventListener("click", unlock, true);
    document.addEventListener("keydown", unlock, true);
  }

  private static initializeIosSampleRate() {
    const initRate = () => {
      if (this.audioContext) {
        const source = this.audioContext.createBufferSource();
        source.buffer = this.audioContext.createBuffer(1, 1, 48000);
        source.connect(this.audioContext.destination);
        source.start(0);
      }
    };

    // iOS Safari で sampleRate が異なることで音が歪むケースがあるため、空の音を鳴らしてからsampleRateを確認する
    initRate();
    if (this.audioContext?.sampleRate !== 44100) {
      this.audioContext = createAudioContext();
      initRate();
    }
  }

  private static obtainAudioSourceNode(): {
    audioElement: HTMLAudioElement;
    sourceNode: MediaElementAudioSourceNode;
    unlocked: boolean;
  } {
    // Return the next object from the pool if one exists.
    if (this.audioSourceNodePool.length) {
      const { element, node } = this.audioSourceNodePool.pop()!;
      return {
        audioElement: element,
        sourceNode: node,
        unlocked: true,
      };
    }

    if (!this.audioContext) {
      throw new Error("uninitialized audioContext");
    }

    const element = new Audio();
    const node = this.audioContext.createMediaElementSource(element);
    return {
      audioElement: element,
      sourceNode: node,
      unlocked: false,
    };
  }

  private static obtainAudioElement(): {
    audioElement: HTMLAudioElement;
    unlocked: boolean;
  } {
    // Return the next object from the pool if one exists.
    if (this.audioElementPool.length) {
      const element = this.audioElementPool.pop()!;
      return {
        audioElement: element,
        unlocked: true,
      };
    }

    const element = new Audio();
    return {
      audioElement: element,
      unlocked: false,
    };
  }

  static isPlayableAudioUrl(url: string): Promise<boolean> {
    const { audioElement, unlocked } = this.obtainAudioElement();

    let cleanuped = false;
    let timeout: number | null = null;
    const cleanup = () => {
      if (cleanuped) {
        return;
      }

      cleanuped = true;
      audioElement.src =
        "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";
      audioElement.autoplay = false;
      audioElement.loop = false;
      audioElement.muted = true;

      audioElement.oncanplay = null;
      audioElement.onerror = null;
      audioElement.onabort = null;

      if (unlocked) {
        this.audioElementPool.push(audioElement);
      }

      if (timeout != null) {
        window.clearTimeout(timeout);
        timeout = null;
      }
    };

    return new Promise((resolve) => {
      audioElement.preload = "auto";
      audioElement.muted = true;

      audioElement.oncanplay = () => {
        resolve(true);
        cleanup();
      };
      audioElement.onerror = () => {
        resolve(false);
        cleanup();
      };
      audioElement.onabort = () => {
        resolve(false);
        cleanup();
      };

      timeout = window.setTimeout(() => {
        resolve(false);
        cleanup();
      }, 10000); // 10 sec

      audioElement.src = url;
      audioElement.load();
    });
  }
}

const createAudioContext = (): AudioContext => {
  if (
    typeof AudioContext === "undefined" &&
    typeof webkitAudioContext !== "undefined"
  ) {
    // iOS < 14.5
    return new webkitAudioContext();
  } else {
    return new AudioContext();
  }
};

const createGain = (
  audioContext: AudioContext,
  options: { defaultVolume: number }
): GainNode => {
  if (
    typeof AudioContext === "undefined" &&
    typeof webkitAudioContext !== "undefined"
  ) {
    // iOS < 14.5
    return audioContext.createGain();
  } else {
    return new GainNode(audioContext, {
      gain: options.defaultVolume,
    });
  }
};

const TIMEOUT_SENTRY_SPAN_MS = 100000; // 100 sec

const injectMetric = (audioElement: HTMLAudioElement) => {
  let sentrySpan: Span | null = null;
  let timer = 0;

  const handleLoadstart = () => {
    if (sentrySpan == null) {
      sentrySpan = startInactiveSpan({
        name: "load-media",
      });

      timer = window.setTimeout(() => {
        sentrySpan?.setStatus({ code: 2, message: "timeout" });
        sentrySpan?.end();
        cleanup();
      }, TIMEOUT_SENTRY_SPAN_MS);
    }
  };

  const handleCanplay = () => {
    window.clearTimeout(timer);
    sentrySpan?.setStatus({ code: 1, message: "ok" });
    sentrySpan?.end();
    cleanup();
  };

  const handleError = () => {
    window.clearTimeout(timer);
    sentrySpan?.setStatus({ code: 2, message: "internal_error" });
    sentrySpan?.end();
    cleanup();
  };

  const cleanup = () => {
    audioElement.removeEventListener("loadstart", handleLoadstart);
    audioElement.removeEventListener("canplay", handleCanplay);
    audioElement.removeEventListener("error", handleError);
    audioElement.removeEventListener("abort", handleError);
  };

  audioElement.addEventListener("loadstart", handleLoadstart);
  audioElement.addEventListener("canplay", handleCanplay);
  audioElement.addEventListener("error", handleError);
  audioElement.addEventListener("abort", handleError);
};

// HTMLAudioElement.volume に isFinite() でない値が入ると落ちるため、フィルタする
const sanitizeFiniteVolume = (x: unknown, defaultValue: number = 1): number => {
  if (typeof x !== "number" || !Number.isFinite(x)) {
    return defaultValue;
  } else {
    return x;
  }
};

export type AudioPlayerOptions = {
  src: string;
  enableWebAudioGain: boolean;
  autoplay?: boolean;
  loop?: boolean;
  muted?: boolean;
  volume?: number;
  disableMesureMetric?: boolean;
  onEnded?: () => void;
};
